import FormatCurrency from 'components/Common/FormatCurrency'
import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import FullWidthCarousel from 'LuxKit/Carousel/FullWidthCarousel'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import GlobalSearchContainerPane, { GlobalSearchContainerPaneContent } from 'components/Search/GlobalSearch/GlobalSearchContainerPane'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { FLASH_HOTELS_AND_TAO_OFFERS_AND_BEDBANK_TYPES } from 'constants/offerList'
import GeoContext from 'contexts/geoContext'
import useFlightDeals from 'hooks/Flights/useFlightDeals'
import useFlightFareLabels from 'hooks/Flights/useFlightFareLabels'
import useOfferList from 'hooks/Offers/useOfferList'
import { take, unique, without } from 'lib/array/arrayUtils'
import { mapAirportToAirportLocation, adjustDestinationName } from 'lib/flights/flightUtils'
import { rem } from 'polished'
import React, { useContext, useMemo, useRef } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import FlightDealTile from '../FlightMerchandising/FlightDealTile'
import type { FlightSearchWidgetSearchData } from '../FlightsSearch/SearchWidget/FlightSearchWidget'
import FlightSearchWidget from '../FlightsSearch/SearchWidget/FlightSearchWidget'
import FlightDealUSPs from './FlightDealUSPs'
import Typography from 'LuxKit/Typography/Typography'
import { useElementHeight } from 'hooks/useElementSize'
import { usePlaceByName } from 'hooks/usePlace'
import OfferListCarousel from 'components/OfferList/OfferListCarousel'
import UrgencyLabel from 'LuxKit/Label/UrgencyLabel'
import SupportTile from 'components/Common/SupportTile/SupportTile'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import uuidV4 from 'lib/string/uuidV4Utils'
import { useAppSelector } from 'hooks/reduxHooks'
import { isMultiCityEnabled } from 'selectors/flightsSelectors'
import Divider from 'LuxKit/Divider'
import { FlightDealInfoCard } from './FlightDealInfoCard'
import OfferUrgencyCountdownLabel from 'components/Common/Labels/OfferUrgencyCountdownLabel'

const FlightDealHero = styled.div`
  padding-top: ${rem(32)};
  position: relative;
  background-color: ${props => props.theme.palette.neutral.default.one};
  padding-bottom: calc(var(--search-offset, 0) + ${rem(32)});

  ${mediaQueryUp.desktop} {
    padding-top: ${rem(40)};
    padding-bottom: calc(var(--search-offset, 0) + ${rem(40)});
  }
`

const HeroImage = styled(ResponsiveImage)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background: linear-gradient(0deg, rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3));
    pointer-events: none;
  }
`

const Container = styled(Pane)`
  position: relative;
  z-index: 1;
`

const FlightDealSearch = styled(LayoutContainer)`
  transform: translateY(-50%);
`

const ContentSection = styled(Pane)`
  padding: ${rem(40)} 0;
`

const LargeContentSection = styled(Pane)`
  padding: ${rem(56)} 0;
`

const FlightDealInfoCardDesktopContainer = styled.div`
  width: ${rem(360)};
  padding: ${rem(16)} ${rem(20)};
  background-color: ${props => props.theme.palette.neutral.default.eight};
  flex-shrink: 0;
`

interface Props {
  flightDeal: App.FlightDeal
}

function FlightDealContent(props: Props) {
  const { flightDeal } = props
  const intl = useIntl()
  const { fareTypes, fareCabins } = useFlightFareLabels()

  let [originFlightDeals] = useFlightDeals('all', { originAirportCode: flightDeal.originAirportCode })
  let [fallbackFlightDeals] = useFlightDeals('all')
  originFlightDeals = without(originFlightDeals, flightDeal)
  fallbackFlightDeals = without(fallbackFlightDeals, flightDeal)
  const otherFlightDeals = useMemo(() => {
    return originFlightDeals.length > 0 ? originFlightDeals : fallbackFlightDeals
  }, [originFlightDeals, fallbackFlightDeals])

  const adjustedDestinationName = adjustDestinationName(flightDeal.destinationName)

  const destinationPlace = usePlaceByName(adjustedDestinationName)
  const multiCityEnabled = useAppSelector(isMultiCityEnabled)

  const alternativeClassFlightDeals = useMemo(() => {
    return otherFlightDeals.filter(deal => deal.originAirportCode === flightDeal.originAirportCode &&
      deal.destinationAirportCode === flightDeal.destinationAirportCode &&
      deal.carrier.code === flightDeal.carrier.code &&
      deal.groupId === flightDeal.groupId,
    )
  }, [flightDeal, otherFlightDeals])

  const [dealFareType, dealClass] = useMemo(() => ([
    fareTypes.find(type => type.value === flightDeal.fareType),
    fareCabins.find(type => type.value === flightDeal.fareClass),
  ]), [flightDeal, fareTypes, fareCabins])

  const { airports } = useContext(GeoContext)

  const searchData = useMemo((): Partial<FlightSearchWidgetSearchData> => {
    const departure = airports.find(airport => airport.code === flightDeal.originAirportCode)
    const arrival = airports.find(airport => airport.code === flightDeal.destinationAirportCode)

    return {
      fareType: dealFareType,
      fareCabin: dealClass,
      filterAirlines: [flightDeal.carrier.code],
      flightDealId: flightDeal.id,
      flights: [{
        id: uuidV4(),
        departureAirport: departure ? mapAirportToAirportLocation(departure) : undefined,
        arrivalAirport: arrival ? mapAirportToAirportLocation(arrival) : undefined,
      }],
      disableCalendarPricing: true,
      dealId: flightDeal.id,
    }
  }, [airports, flightDeal, dealClass, dealFareType])

  const destinationOfferList = useOfferList({
    offerTypes: FLASH_HOTELS_AND_TAO_OFFERS_AND_BEDBANK_TYPES,
    destinationId: destinationPlace?.id,
  }, { disabled: !destinationPlace })
  const isSaleActive = useMemo(() => new Date() < new Date(flightDeal.salesEndDate), [flightDeal.salesEndDate])
  const showBundleHotel = useMemo(() => {
    return isSaleActive && (destinationOfferList.fetching || destinationOfferList.offerIds.length > 0)
  }, [isSaleActive, destinationOfferList])

  const finalOfferList: App.OfferList = useMemo(() => {
    return {
      error: destinationOfferList.error,
      fetching: destinationOfferList.fetching,
      key: 'flightDealOffers',
      offerIds: take(unique(destinationOfferList.offerIds), 16),
    }
  }, [destinationOfferList])

  const containerRef = useRef<HTMLDivElement>(null)
  const searchHeight = useElementHeight(containerRef)

  const searchOffset = searchHeight ? (searchHeight / 2) : undefined

  const exclusiveOfferHeading = intl.formatMessage(
    {
      id: 'aUWFThDsDT',
      defaultMessage: '{carrierName} exclusive offer',
      description: 'Subheading for flight deal hero section showing carrier name',
    },
    { carrierName: flightDeal.carrier.name },
  )

  const saleEndedLabel = intl.formatMessage({
    id: 'BLi8qmlY0W',
    defaultMessage: 'This sale has ended',
    description: 'Label shown when a flight deal sale period has ended',
  })

  const supportTitle = intl.formatMessage({
    id: '4I4Y0SEnoL',
    defaultMessage: 'Call us to find out more or book this offer today',
    description: 'Title for support section on flight deal landing page',
  })

  const supportDescription = intl.formatMessage({
    id: 'p0rDSvJ2Em',
    defaultMessage: 'Our dedicated flights team are available anytime, 24 hours, 7 days a week.',
    description: 'Description for support section on flight deal landing page',
  })

  const termsHeading = intl.formatMessage({
    id: 'N/1uQfvTBD',
    defaultMessage: 'Terms & Conditions',
    description: 'Heading for terms and conditions section on flight deal page',
  })

  const otherFlightDealsHeading = originFlightDeals.length > 0
    ? (
        <FormattedMessage
          id="gqauyPF5vc"
          defaultMessage="Explore more <italic>limited-time</italic> offers from {originName}"
          description="Heading for more flight deal offers section showing origin city"
          values={{
            italic: (chunks: React.ReactNode) => <i>{chunks}</i>,
            originName: flightDeal.originName,
          }}
        />
      )
    : (
        <FormattedMessage
          id="kMvPgAvMCD"
          defaultMessage="Explore more <italic>limited-time</italic> offers"
          description="Heading for more flight deal offers section without origin city"
          values={{
            italic: (chunks: React.ReactNode) => <i>{chunks}</i>,
          }}
        />
      )

  return <Pane>
    <FlightDealHero style={{ '--search-offset': rem(searchOffset ?? 0) } as any}>
      {!!flightDeal.heroImage?.id && <HeroImage
        id={flightDeal.heroImage.id}
        fit="center"
      />}

      <LayoutContainer style={{ transform: 'translateZ(0)' }}>
        <CSSBreakpoint max="mobile">
          <Group direction="vertical" horizontalAlign="center" gap={16}>
            <VerticalSpacer gap={8}>
              <Heading variant="heading5" align="center" colour="neutral-eight">
                {exclusiveOfferHeading}
              </Heading>

              <Heading variant="heading2" align="center" colour="neutral-eight">
                <FormattedMessage
                  id="DseW7MOVh9"
                  defaultMessage="{origin} to {destination} from {price} <italic>{fareType}*</italic>"
                  description="Flight deal hero heading showing route, price and fare type on mobile"
                  values={{
                    origin: flightDeal.originName,
                    destination: adjustedDestinationName,
                    price: <FormatCurrency value={flightDeal.perAdultPrice} />,
                    italic: (chunks: React.ReactNode) => <Typography as="i" format="lowercase">{chunks}</Typography>,
                    fareType: dealFareType?.label,
                  }}
                />
              </Heading>
            </VerticalSpacer>

            {isSaleActive && <OfferUrgencyCountdownLabel
              endDate={flightDeal.salesEndDate}
              tagPlace="flight-deal-content"
            />}
            {!isSaleActive && <UrgencyLabel variant="critical2">
              {saleEndedLabel}
            </UrgencyLabel>}
          </Group>
        </CSSBreakpoint>

        <CSSBreakpoint min="tablet">
          <Group direction="horizontal" gap="0 32" verticalAlign="center" horizontalAlign="space-between">
            <Group direction="vertical" gap={16} horizontalAlign="start">
              <VerticalSpacer gap={8}>
                <Heading variant="heading5" colour="neutral-eight">
                  {exclusiveOfferHeading}
                </Heading>

                <Heading variant="heading1" colour="neutral-eight">
                  <FormattedMessage
                    id="we/Tv3Ar3e"
                    defaultMessage="{origin} to {destination} from {price} <italic>{fareType}*</italic>"
                    description="Flight deal hero heading showing route, price and fare type on desktop"
                    values={{
                      origin: flightDeal.originName,
                      destination: adjustedDestinationName,
                      price: <FormatCurrency value={flightDeal.perAdultPrice} />,
                      italic: (chunks: React.ReactNode) => <Typography as="i" format="lowercase">{chunks}</Typography>,
                      fareType: dealFareType?.label,
                    }}
                  />
                </Heading>
              </VerticalSpacer>

              {isSaleActive && <OfferUrgencyCountdownLabel
                endDate={flightDeal.salesEndDate}
                tagPlace="flight-deal-content"
              />}
              {!isSaleActive && <UrgencyLabel variant="critical2">
                {saleEndedLabel}
              </UrgencyLabel>}
            </Group>

            <FlightDealInfoCardDesktopContainer>
              <FlightDealInfoCard deal={flightDeal} alternativeClassFlightDeals={alternativeClassFlightDeals} />
            </FlightDealInfoCardDesktopContainer>
          </Group>
        </CSSBreakpoint>
      </LayoutContainer>
    </FlightDealHero>

    <Container
      style={{ height: searchOffset }}
    >
      <FlightDealSearch ref={containerRef}>
        <GlobalSearchContainerPane displayMobile>
          <GlobalSearchContainerPaneContent>
            <Group direction="vertical" gap="16 0">
              <CSSBreakpoint max="mobile">
                <VerticalSpacer gap={16}>
                  <FlightDealInfoCard deal={flightDeal} alternativeClassFlightDeals={alternativeClassFlightDeals} />
                  <Divider kind="primary" />
                </VerticalSpacer>
              </CSSBreakpoint>

              <FlightSearchWidget
                searchData={searchData}
                showMultiCity={multiCityEnabled}
              />
            </Group>
          </GlobalSearchContainerPaneContent>
        </GlobalSearchContainerPane>
      </FlightDealSearch>
    </Container>

    <ContentSection>
      <LayoutContainer>
        <SupportTile
          title={supportTitle}
          description={supportDescription}
        />
      </LayoutContainer>
    </ContentSection>

    {showBundleHotel && <LargeContentSection>
      <VerticalSpacer gap={32}>
        <LayoutContainer>
          <Heading variant="heading2">
            <FormattedMessage
              id="T55CtNCmB+"
              defaultMessage="Bundle hotel + flights to <italic>save</italic>"
              description="Heading for bundle hotel and flights section on flight deal page"
              values={{
                italic: (chunks: React.ReactNode) => <i>{chunks}</i>,
              }}
            />
          </Heading>
        </LayoutContainer>
        <OfferListCarousel
          overrideOfferList={finalOfferList}
        />
      </VerticalSpacer>
    </LargeContentSection>}
    {otherFlightDeals.length > 0 && <ContentSection>
      <VerticalSpacer gap={32}>
        <LayoutContainer>
          <Heading variant="heading2">{otherFlightDealsHeading}</Heading>
        </LayoutContainer>
        <FullWidthCarousel gap={20} width="75vw" desktopWidth="320px" snap="start">
          {otherFlightDeals.map((deal, index) => (
            <FlightDealTile key={deal.id} flightDeal={deal} position={index} />
          ))}
        </FullWidthCarousel>
      </VerticalSpacer>
    </ContentSection>}
    <LayoutContainer>
      <ContentSection>
        <VerticalSpacer gap={32}>
          <Heading variant="heading2">
            <FormattedMessage
              id="40ldASbWMa"
              defaultMessage="Why you should <italic>book</italic> with us"
              description="Heading for reasons to book section on flight deal page"
              values={{
                italic: (chunks: React.ReactNode) => <i>{chunks}</i>,
              }}
            />
          </Heading>
          <FlightDealUSPs />
        </VerticalSpacer>
      </ContentSection>
      <ContentSection>
        <VerticalSpacer gap={8}>
          <Heading variant="heading6">{termsHeading}</Heading>
          <BodyText variant="small" colour="neutral-three">
            {flightDeal.termsAndConditions}
          </BodyText>
        </VerticalSpacer>
      </ContentSection>
    </LayoutContainer>
  </Pane>
}

export default FlightDealContent
